import { useMutation, useQuery } from '@apollo/client/react';
import { type FC, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  GetAccountBusinessUnitsDocument,
  type GetAccountBusinessUnitsQueryResult,
  type GetAccountBusinessUnitsQueryVariables
} from '@zen/Accounts/AccountDetails/graphql';
import CreateEditModal from '@zen/Components/EditCreateModal';
import type { CreateEditModalConfiguration } from '@zen/Components/EditCreateModal/types';
import SecretsModal from '@zen/Components/SecretsModal';
import { Button, Table } from '@zen/DesignSystem';
import type { AccountSettingsPermissions } from '@zen/graphql/types.generated';
import { useNotification } from '@zen/utils/hooks/useNotification';
import { performMutation } from '@zen/utils/performMutation';
import type { Optional } from '@zen/utils/typescript';

import CreateWebhook from './CreateWebhook';
import EditWebhook from './EditWebhook';
import {
  DeleteWebhookDocument,
  type DeleteWebhookMutationResult,
  type DeleteWebhookMutationVariables,
  DisableWebhookDocument,
  type DisableWebhookMutationResult,
  type DisableWebhookMutationVariables,
  EnableWebhookDocument,
  type EnableWebhookMutationResult,
  type EnableWebhookMutationVariables,
  GetWebhooksDocument,
  type GetWebhooksQueryResult,
  type GetWebhooksQueryVariables
} from './graphql';
import { getColumns } from './tableConfiguration';
import type { Webhook } from './types';

interface Props {
  accountId: string;
  permissions?: AccountSettingsPermissions | null;
}

const Webhooks: FC<Props> = ({ accountId, permissions }) => {
  const { addError, addSuccess } = useNotification();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();

  const canCreateWebhooks: boolean = !!permissions?.canCreateWebhook?.value;
  const canEditWebhooks: boolean = !!permissions?.canEditWebhook?.value;

  const { data: businessUnitsData, loading: businessUnitsLoading } = useQuery<
    GetAccountBusinessUnitsQueryResult,
    GetAccountBusinessUnitsQueryVariables
  >(GetAccountBusinessUnitsDocument, {
    variables: { accountId, first: 20 }
  });

  const businessUnitIds: string[] = (businessUnitsData?.accountBusinessUnits?.nodes || []).map(
    (businessUnit) => businessUnit?.id || ''
  );
  const accountHasBusinessUnits: boolean = businessUnitIds.length > 0;

  const [modalConfiguration, setModalConfiguration] = useState<CreateEditModalConfiguration<Webhook>>();

  const secretToken: Optional<string> = state?.secretToken;

  const { data, loading, error } = useQuery<GetWebhooksQueryResult, GetWebhooksQueryVariables>(GetWebhooksDocument, {
    variables: {
      accountIds: accountHasBusinessUnits ? businessUnitIds : [accountId]
    },
    skip: businessUnitsLoading
  });

  const [deleteWebhook] = useMutation<DeleteWebhookMutationResult, DeleteWebhookMutationVariables>(DeleteWebhookDocument);
  const [enableWebhook] = useMutation<EnableWebhookMutationResult, EnableWebhookMutationVariables>(EnableWebhookDocument);
  const [disableWebhook] = useMutation<DisableWebhookMutationResult, DisableWebhookMutationVariables>(DisableWebhookDocument);

  const webhooks: Webhook[] = data?.webhookSubscriptionsWebhooks || [];

  const handleEdit = (webhook: Webhook): void => {
    setModalConfiguration({
      type: 'edit',
      initialValues: webhook,
      title: 'Edit Webhook'
    });
  };

  const handleCreate = (): void => {
    setModalConfiguration({
      type: 'create',
      title: 'Create Webhook'
    });
  };

  const handleCloseModal = (): void => setModalConfiguration(undefined);

  const handleCloseSecretModal = (): void | Promise<void> => navigate(pathname, { replace: true });

  const handleToggle = async (webhookId: string, isDisabled: boolean): Promise<void> => {
    const mutationFn = isDisabled ? enableWebhook : disableWebhook;
    const successMessage = isDisabled ? 'Webhook has been enabled.' : 'Webhook has been disabled.';

    await performMutation({
      mutationFn: () =>
        mutationFn({
          variables: {
            input: {
              webhookId
            }
          },
          refetchQueries: ['getWebhooks'],
          awaitRefetchQueries: true
        }),
      onError: () => addError(),
      onSuccess: () => {
        addSuccess(successMessage);
      }
    });
  };

  const handleDelete = async (webhookId: string): Promise<void> => {
    await performMutation({
      mutationFn: () =>
        deleteWebhook({
          variables: {
            input: {
              webhookId
            }
          },
          refetchQueries: ['getWebhooks'],
          awaitRefetchQueries: true
        }),
      onError: () => addError(),
      onSuccess: () => {
        addSuccess('Webhook has been deleted.');
      }
    });
  };

  return (
    <div data-testid="webhooks">
      <Table
        actions={
          canCreateWebhooks && (
            <Button onClick={handleCreate} size="compact">
              Create Webhook
            </Button>
          )
        }
        columns={getColumns({
          accountHasBusinessUnits,
          canManage: canEditWebhooks,
          onEdit: handleEdit,
          onDelete: handleDelete,
          onToggle: handleToggle
        })}
        data={webhooks}
        error={!!error}
        loading={loading}
        tableId="webhooks"
        title="Webhooks"
        totalCountConfig={{ totalCount: webhooks.length, entityName: 'Webhook' }}
      />
      <CreateEditModal
        configuration={modalConfiguration}
        createForm={({ onClose }) => (
          <CreateWebhook accountHasBusinessUnits={accountHasBusinessUnits} accountId={accountId} onClose={onClose} />
        )}
        editForm={({ onClose, item }) => <EditWebhook accountId={accountId} item={item} onClose={onClose} />}
        onClose={handleCloseModal}
      />
      {secretToken && <SecretsModal isOpened={!!secretToken} onClose={handleCloseSecretModal} secret={secretToken} />}
    </div>
  );
};

export default Webhooks;
